import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Check, Pencil, Ban, Upload, Download, Layers } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageIntro, PanelHead, Modal, Field, SelectField, Badge, Pagination } from "../components/UI";
import { usePagination } from "../lib/usePagination";
import { BulkUploadDialog } from "../components/BulkUpload";
import { useAuth } from "../context/AuthContext";

const TYPES = ["Expense", "COGS", "Asset", "Other"];
const TYPE_TONE = { Expense: "blue", COGS: "amber", Asset: "green", Other: "neutral" };

const COA_TEMPLATE = {
  headers: ["Code", "Name", "Type", "TaxType"],
  example: [
    ["5000", "Food Purchases", "COGS", "Tax Exempt"],
    ["6000", "Beverage Purchases", "COGS", "Tax Exempt"],
    ["6100", "Cleaning Supplies", "Expense", "PPN 11%"],
  ],
};

export default function CoaPage() {
  const { user } = useAuth();
  const canManage = ["admin", "finance"].includes(user?.role);
  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [modal, setModal] = useState(null); // "form" | "upload" | "assign"
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(defaultForm());
  const [saving, setSaving] = useState(false);
  // assign tool
  const [assignAccount, setAssignAccount] = useState("");
  const [assignItemIds, setAssignItemIds] = useState([]);
  const [assignSearch, setAssignSearch] = useState("");
  const [unassigned, setUnassigned] = useState([]);
  const [unassignedTotal, setUnassignedTotal] = useState(0);
  const [loadingUnassigned, setLoadingUnassigned] = useState(false);

  const loadUnassigned = (q = "") => {
    setLoadingUnassigned(true);
    api.get("/items/unassigned-account", { params: q ? { search: q } : {} })
      .then((r) => { setUnassigned(r.data.items || []); setUnassignedTotal(r.data.total || 0); })
      .catch(() => {})
      .finally(() => setLoadingUnassigned(false));
  };

  const openAssign = () => {
    setAssignItemIds([]);
    setAssignAccount("");
    setAssignSearch("");
    loadUnassigned("");
    setModal("assign");
  };

  const assignOne = async (itemId, code) => {
    if (!code) return;
    try {
      await api.post("/items/assign-account", { item_ids: [itemId], account_code: code });
      toast.success(`Assigned ${code}`);
      setUnassigned((prev) => prev.filter((i) => i.id !== itemId));
      setUnassignedTotal((t) => Math.max(0, t - 1));
      setAssignItemIds((prev) => prev.filter((x) => x !== itemId));
      api.get("/items").then((r) => setItems(r.data || []));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const load = () => api.get("/coa").then((r) => setList(r.data || []));
  useEffect(() => {
    load();
    api.get("/items").then((r) => setItems(r.data || [])).catch(() => {});
  }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(defaultForm());
    setModal("form");
  };
  const openEdit = (a) => {
    setEditingId(a.id);
    setForm({ code: a.code, name: a.name, type: a.type, default_tax_type: a.default_tax_type || "", is_active: a.is_active !== false });
    setModal("form");
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.code.trim() || !form.name.trim()) return toast.error("Code and name are required");
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/coa/${editingId}`, {
          name: form.name, type: form.type,
          default_tax_type: form.default_tax_type, is_active: form.is_active,
        });
        toast.success("Account updated");
      } else {
        await api.post("/coa", {
          code: form.code, name: form.name, type: form.type,
          default_tax_type: form.default_tax_type, is_active: form.is_active,
        });
        toast.success("Account created");
      }
      setModal(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (a) => {
    if (!window.confirm(`Deactivate account ${a.code}?`)) return;
    try {
      await api.post(`/coa/${a.id}/deactivate`);
      toast.success(`${a.code} deactivated`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/coa/export", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = "chart_of_accounts.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    }
  };

  const submitAssign = async () => {
    if (!assignAccount) return toast.error("Pick an account");
    if (assignItemIds.length === 0) return toast.error("Select at least one item");
    try {
      const { data } = await api.post("/items/assign-account", {
        item_ids: assignItemIds, account_code: assignAccount,
      });
      toast.success(`Assigned ${data.account_code} to ${data.updated} item(s)`);
      setModal(null);
      setAssignItemIds([]);
      setAssignAccount("");
      api.get("/items").then((r) => setItems(r.data || []));
      loadUnassigned(assignSearch);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((a) => {
      if (typeFilter && a.type !== typeFilter) return false;
      if (activeFilter === "active" && a.is_active === false) return false;
      if (activeFilter === "inactive" && a.is_active !== false) return false;
      if (!q) return true;
      return (a.code || "").toLowerCase().includes(q) || (a.name || "").toLowerCase().includes(q);
    });
  }, [list, search, typeFilter, activeFilter]);
  const pager = usePagination(filtered, 100, `${search}|${typeFilter}|${activeFilter}`);

  const assignItemsFiltered = useMemo(() => {
    const q = assignSearch.trim().toLowerCase();
    if (!q) return items.slice(0, 50);
    return items.filter((i) => (i.name || "").toLowerCase().includes(q) || (i.sku || "").toLowerCase().includes(q)).slice(0, 50);
  }, [items, assignSearch]);

  return (
    <>
      <PageIntro
        eyebrow="Finance · chart of accounts"
        title="Chart of Accounts"
        subtitle="Map inventory items and purchases to account codes for clean Xero Bill uploads."
        testid="coa-title"
        action={
          <div className="action-cluster">
            <button data-testid="coa-export-button" className="secondary-button" onClick={exportCsv}>
              <Download size={16} /> Export CSV
            </button>
            {canManage && (
              <>
                <button data-testid="coa-assign-button" className="secondary-button" onClick={openAssign}>
                  <Layers size={16} /> Assign to items
                </button>
                <button data-testid="coa-upload-button" className="secondary-button" onClick={() => setModal("upload")}>
                  <Upload size={16} /> Import CSV
                </button>
                <button data-testid="coa-add-button" className="primary-button" onClick={openNew}>
                  <Plus size={17} /> New account
                </button>
              </>
            )}
          </div>
        }
      />
      <section className="panel">
        <div className="toolbar">
          <div className="search-box">
            <Search size={17} />
            <input
              data-testid="coa-search-input"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by code or name..."
            />
          </div>
          <select data-testid="coa-type-filter" value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} style={selStyle}>
            <option value="">All types</option>
            {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select data-testid="coa-active-filter" value={activeFilter} onChange={(e) => setActiveFilter(e.target.value)} style={selStyle}>
            <option value="all">All status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
        <PanelHead title="Accounts" detail={`${filtered.length} account(s)`} />
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Default tax</th>
                <th>Status</th>
                {canManage && <th style={{ textAlign: "right" }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={canManage ? 6 : 5} style={{ textAlign: "center", padding: 40 }}>No accounts yet.</td></tr>
              )}
              {pager.pageItems.map((a) => (
                <tr key={a.id} data-testid={`coa-row-${a.id}`}>
                  <td><strong>{a.code}</strong></td>
                  <td>{a.name}</td>
                  <td><Badge tone={TYPE_TONE[a.type] || "neutral"}>{a.type}</Badge></td>
                  <td><small>{a.default_tax_type || "-"}</small></td>
                  <td>
                    <Badge tone={a.is_active === false ? "neutral" : "green"}>
                      {a.is_active === false ? "Inactive" : "Active"}
                    </Badge>
                  </td>
                  {canManage && (
                    <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                      <button data-testid={`coa-edit-${a.id}`} className="small-button" onClick={() => openEdit(a)}>
                        <Pencil size={12} /> Edit
                      </button>
                      {a.is_active !== false && (
                        <button data-testid={`coa-deactivate-${a.id}`} className="small-button" style={{ marginLeft: 6 }} onClick={() => deactivate(a)}>
                          <Ban size={12} /> Deactivate
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Pagination page={pager.page} pageCount={pager.pageCount} total={pager.total} pageSize={pager.pageSize} onChange={pager.setPage} testid="coa-pagination" />
      </section>

      {modal === "form" && (
        <Modal title={editingId ? "Edit account" : "New account"} onClose={() => setModal(null)}>
          <form className="form-grid" onSubmit={save}>
            <Field label="Account code" testid="coa-code-input" value={form.code} onChange={(v) => setForm({ ...form, code: v })} placeholder="e.g. 5000" required disabled={!!editingId} />
            <Field label="Account name" testid="coa-name-input" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="e.g. Food Purchases" required />
            <SelectField label="Type" testid="coa-type-select" value={form.type} onChange={(v) => setForm({ ...form, type: v })} options={TYPES} />
            <Field label="Default tax type (optional)" testid="coa-tax-input" value={form.default_tax_type} onChange={(v) => setForm({ ...form, default_tax_type: v })} placeholder="Tax Exempt / PPN 11%" />
            <label className="field" style={{ gridColumn: "1/-1", flexDirection: "row", alignItems: "center", gap: 8 }}>
              <input type="checkbox" data-testid="coa-active-checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />
              <span>Active</span>
            </label>
            <div className="form-actions">
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Cancel</button>
              <button data-testid="coa-save-button" className="primary-button" type="submit" disabled={saving}>
                <Check size={16} /> {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {modal === "upload" && (
        <BulkUploadDialog
          title="Import Chart of Accounts (CSV)"
          endpoint="/coa/bulk-upload"
          templateName="coa_template.csv"
          templateHeaders={COA_TEMPLATE.headers}
          templateExample={COA_TEMPLATE.example}
          instructions={<>Columns: <b>Code, Name, Type, TaxType</b>. Existing codes are updated, new ones created. Type must be Expense / COGS / Asset / Other.</>}
          onClose={() => setModal(null)}
          onSuccess={load}
          testid="coa-upload"
        />
      )}

      {modal === "assign" && (
        <Modal title="Item belum di-assign COA" onClose={() => setModal(null)}>
          <p className="form-hint" data-testid="assign-total-count">
            <strong>{unassignedTotal}</strong> item belum di-assign. Pilih beberapa lalu terapkan satu COA, atau assign per baris.
          </p>
          <datalist id="coa-code-options">
            {list.filter((a) => a.is_active !== false).map((a) => (
              <option key={a.id} value={a.code}>{a.code} - {a.name}</option>
            ))}
          </datalist>
          <div className="form-grid">
            <div className="search-box" style={{ gridColumn: "1/-1", maxWidth: "none" }}>
              <Search size={16} />
              <input
                data-testid="assign-item-search"
                value={assignSearch}
                onChange={(e) => setAssignSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") loadUnassigned(assignSearch); }}
                placeholder="Cari nama / kode item, tekan Enter..."
              />
            </div>
            <label className="field" style={{ gridColumn: "1/-1" }}>
              <span>Bulk assign — COA untuk item terpilih</span>
              <input
                list="coa-code-options"
                data-testid="assign-account-select"
                value={assignAccount}
                onChange={(e) => setAssignAccount(e.target.value)}
                placeholder="Ketik kode / nama akun..."
                style={{ padding: 10, border: "1px solid var(--line)", borderRadius: 6 }}
              />
            </label>
            <div className="table-wrap" style={{ gridColumn: "1/-1", maxHeight: 360, overflow: "auto" }}>
              <table>
                <thead>
                  <tr>
                    <th style={{ width: 34 }}></th>
                    <th>Item</th>
                    <th>Category</th>
                    <th>Assign COA</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingUnassigned && <tr><td colSpan={4} style={{ textAlign: "center", padding: 30 }}>Loading...</td></tr>}
                  {!loadingUnassigned && unassigned.length === 0 && (
                    <tr><td colSpan={4} style={{ textAlign: "center", padding: 30 }} data-testid="assign-empty">Semua item sudah di-assign 🎉</td></tr>
                  )}
                  {unassigned.map((i) => (
                    <tr key={i.id} data-testid={`assign-row-${i.id}`}>
                      <td>
                        <input
                          type="checkbox"
                          data-testid={`assign-item-${i.id}`}
                          checked={assignItemIds.includes(i.id)}
                          onChange={(e) => setAssignItemIds((prev) => e.target.checked ? [...prev, i.id] : prev.filter((x) => x !== i.id))}
                        />
                      </td>
                      <td><strong>{i.name}</strong><small>{i.sku}</small></td>
                      <td><small>{i.category || "-"}</small></td>
                      <td>
                        <input
                          list="coa-code-options"
                          data-testid={`assign-row-coa-${i.id}`}
                          placeholder="Ketik & pilih..."
                          onChange={(e) => { const v = e.target.value; if (list.some((a) => a.code === v)) assignOne(i.id, v); }}
                          style={{ padding: 8, border: "1px solid var(--line)", borderRadius: 5, width: 180 }}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="form-actions">
              <span style={{ marginRight: "auto", fontSize: 12, color: "var(--muted)" }} data-testid="assign-selected-count">{assignItemIds.length} selected</span>
              <button type="button" className="secondary-button" onClick={() => setModal(null)}>Close</button>
              <button type="button" data-testid="assign-apply-button" className="primary-button" onClick={submitAssign}>
                <Check size={16} /> Apply to selected
              </button>
            </div>
          </div>
        </Modal>
      )}
    </>
  );
}

const selStyle = { border: "1px solid var(--line)", borderRadius: 6, padding: "0 12px", font: "13px 'DM Sans'", background: "#fff" };

function defaultForm() {
  return { code: "", name: "", type: "Expense", default_tax_type: "", is_active: true };
}
